import { logException } from "@wss/error-tracking";
import {
  ComponentType,
  lazy,
  LazyExoticComponent,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useState,
  Context,
  createContext as createReactContext,
  useContext as useReactContext,
} from "react";
import React, {
  createRoot as createReactRoot,
  hydrateRoot,
  Root,
} from "react-dom/client";

const roots = new Map<Element, Root>();

export function createRoot(
  elem: unknown,
  opts?: Parameters<typeof createReactRoot>[1],
): Root {
  if (!(elem instanceof Element)) {
    const error = new Error(
      `"elem" argument is not an instance of an Element. Is it in the DOM?`,
    );
    logException(error);
    throw error;
  }
  const cache = roots.get(elem) ?? createReactRoot(elem, opts);
  if (!roots.get(elem)) roots.set(elem, cache);
  return {
    ...cache,
    render(component: ReactNode): void {
      cache.render(component);
      if (module.hot) {
        /** Runs when a module has been updated */
        module.hot.dispose(() => {
          cache.render(component);
        });
      }
    },
  };
}

type RenderAtRootProps = {
  container: Element;
  children: ReactNode;
};

export function RenderAtRoot({ container, children }: RenderAtRootProps) {
  useLayoutEffect(
    function initRenderAtRootEffect() {
      const cache = roots.get(container);
      if (!cache) {
        const root = createRoot(container);
        if (!roots.has(container)) roots.set(container, root);
        root.render(children);
      } else {
        cache.render(children);
      }
    },
    [container, children],
  );

  return <></>;
}

type HydrateAtRootProps = {
  container: Element;
  children: ReactNode;
};

export function HydrateAtRoot({ container, children }: HydrateAtRootProps) {
  useLayoutEffect(
    function initHydrateAtRootEffect() {
      const cache = roots.get(container);
      if (!cache) {
        roots.set(container, hydrateRoot(container, children));
      } else {
        cache.render(children);
      }
    },
    [container, children],
  );

  return <></>;
}

export function hydrateIntoContainer(container: Element, children: ReactNode) {
  const cache = roots.get(container);
  if (!cache) {
    roots.set(container, hydrateRoot(container, children));
  } else {
    cache.render(children);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const contexts = new Map<string, Context<any>>();

export function createHypernovaContext<T>(
  name: string,
  value?: T,
): Context<T | undefined> {
  let cache = contexts.get(name);
  if (!cache) {
    cache = createReactContext<T | undefined>(value);
    contexts.set(name, cache);
  }
  return cache;
}

export type PageDataSelectorFn<T, U> = (data: T) => U;

/** Used for child components of a page to consumer the data provided from the server.
 *
 * **Basic Usage**
 * @example
 * ```ts
 * const data = usePageData<MyPageDataType>("[HypernovaName]");
 * ```
 *
 * **Preferred Usage**
 *
 * We can create a wrapper utility so other components no longer need to pass the Hypernova Component name.
 * @example
 * ```ts
 * export function useMyPageData(): MyPageDataType {
 *  return usePageData<MyPageDataType>("[HypernovaName]");
 * }
 * // In your components
 * const data = useMyPageData();
 * ```
 *
 * **With a Selector**
 *
 * You can select a value from the data so you are only consuming what you need. This works best with the preferred example above
 * @example
 * ```ts
 * import { PageDataSelectorFn } from "@wss/react"; // ➕ Add Import
 *
 * export function useMyPageData<U>( // ➕ Add Generic
 *  selector: PageDataSelectorFn<MyPageDataType, U> // ➕ Add Argument
 * ): U { // ➕ Update Return Value
 *  return usePageData<MyPageDataType, U>("[HypernovaName]", selector); // ➕ Add Generic & Selector
 * }
 * // In your components
 * const data = useMyPageData((data) => data.myKey); // ➕ Add selector function
 * ```
 *
 * **Reduce Function ReDeclarations**
 *
 * When using a selector in the previous example the function will be redefined each time the component rerenders. This can lead to increased memory usage in components that rerender often. We can fix that one of two ways.
 *
 * 1. Define the selector outside of the component. The function can not have any dependencies.
 * @example
 * ```ts
 * function selector(data: MyPageDataType) { return data.myKey }
 *
 * function MyComponent() {
 *  const data = useMyPageData(selector);
 * }
 * ```
 *
 * 2. Memoize the callback with it's dependencies.
 * @example
 * ```ts
 * function MyComponent({ index }: {index: number}) {
 *  const data = useMyPageData(useCallback((data) => data[index], [index]));
 * }
 * ```
 *
 * **Testing Components**
 *
 * To mock the hypernova data import the utility from "@wss/react" in your test file.
 * @example
 * ```ts
 * import { usePageData } from "@wss/react";
 * ```
 *
 * Then mock the import,
 * @example
 * ```ts
 * jest.mock("@wss/react");
 * ```
 *
 * Then create a variable that has the correct typing.
 * @example
 * ```ts
 * const mockedUsePageData = usePageData as jest.MockedFunctionDeep<
 *   typeof usePageData
 * >;
 * ```
 *
 * Finally, set the data either in a test or beforeAll the test with `.mockReturnValue`.
 * @example
 * ```ts
 * mockedUsePageData.mockReturnValue({some: "data"})
 * ```
 */
export function usePageData<T>(name: string): T;
export function usePageData<T, U>(
  name: string,
  selector: PageDataSelectorFn<T, U>,
): U;
export function usePageData<T, U>(
  name: string,
  selector?: PageDataSelectorFn<T, U>,
): U {
  if (window.location !== undefined) {
    const Context = contexts.get(name) as Context<T | undefined>;
    /** We can safely run this inside of a condition because this will always be true on the client. */
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const context = useReactContext<T | undefined>(Context);
    if (!context) {
      throw new Error(
        "Context has no value. Are you sure the server provided some data?",
      );
    }

    return selector ? selector(context) : (context as U);
  }

  return selector ? selector(globalThis.hypernova) : globalThis.hypernova;
}

type LazyLoadedComponent<T> = LazyExoticComponent<ComponentType<T>> & {
  preload: Parameters<typeof lazy>[0];
};

export function lazyWithPreload(factory: Parameters<typeof lazy>[0]) {
  const Component = lazy(factory) as LazyLoadedComponent<
    [Awaited<ReturnType<typeof factory>>["default"]]
  >;
  Component.preload = factory;
  return Component;
}

type LoadingTrackingProps = {
  onLoad: () => void;
  onComplete: () => void;
};

export function SuspenseLoadTracking({
  children,
  onComplete,
  onLoad,
}: PropsWithChildren<LoadingTrackingProps>) {
  useEffect(
    function initLoadingTracking() {
      onLoad();
      return onComplete;
    },
    [onLoad, onComplete],
  );

  return <>{children}</>;
}

/** This is a work around for the limited support renderToString has for suspense.
 * @important This should only be used for code that can not be lazy loaded via a user interaction. It will cause extra re-renders.
 * @see {@link https://react.dev/reference/react-dom/server/renderToString#caveats React Docs}
 * @see {@link https://stackoverflow.com/questions/75316236/how-can-i-suspend-component-on-the-server-side-but-render-it-on-the-client-side Stackoverflow Issue About Error}
 */
export function ClientOnly({
  children,
}: PropsWithChildren): JSX.Element | null {
  const [isClient, setIsClient] = useState<boolean>(false);

  useEffect(function initIsClient() {
    setIsClient(true);
  }, []);

  if (!isClient) return null;

  return <>{children}</>;
}

export function createContext<State>() {
  const Context = createReactContext<State | null>(null);

  function useContext() {
    const context = useReactContext(Context);
    if (!context) throw new Error("Missing Context.Provider in Component Tree");
    return context;
  }

  return [Context, useContext] as const;
}
